import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button, Typography, Paper } from '@mui/material';
import api from '../services/api';

// Landing page for the Akahu OAuth redirect. Akahu sends the user back here
// with a short-lived authorization code, which we pass to the backend to
// exchange for an enduring user token.
export default function AkahuCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // The code is single-use, so guard against double execution (StrictMode)
  const exchangeStarted = useRef(false);

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');
    const event = searchParams.get('event');

    if (oauthError) {
      setError(`Akahu authorisation was not completed (${oauthError}).`);
      return;
    }
    if (event === 'REVOKE') {
      api.post('/akahu/oauth/revoke', { state })
        .then(() => navigate('/settings?akahu=revoked', { replace: true }))
        .catch(err => setError(err.response?.data?.error || 'Could not confirm disconnection.'));
      return;
    }
    if (!code) {
      setError('No authorisation code was found in the URL.');
      return;
    }

    api.post('/akahu/oauth/exchange', {
      code,
      state,
      redirect_uri: `${window.location.origin}/auth/akahu/callback`
    })
      .then(() => navigate('/settings?akahu=connected', { replace: true }))
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to complete the Akahu connection.');
      });
  }, [searchParams, navigate]);

  return (
    <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 8, textAlign: 'center' }}>
      {error ? (
        <>
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/settings')}>
            Back to Settings
          </Button>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Completing your Akahu connection…</Typography>
        </Box>
      )}
    </Paper>
  );
}

